import { useNavigate } from '@tanstack/react-router';
import { Button, Stack } from '@optimus-web/core';
import {
    FormCheckboxField,
    FormInputField,
    HOSPITALITY_CLOUD_TERMS_AND_CONDITIONS_URL,
    LocalizedText,
    showSuccessAlert,
    Form,
} from '@mews/framework';
import { TextKey, Cookie } from '@mews-commander/enums';
import { GeneralUserConsentType, useAppConfiguration, useRepositoryMutation } from '@mews-commander/data-access';
import { PasswordField } from '@mews-commander/form';
import { NewTabLink, OptimusProvider, OverlayedLoader, ResponsiveTwoColumns } from '@mews-commander/ui';
import { getCookie } from '@mews-commander/utils';
import { createSignUpResolver, FormField, SignUpFormValues } from './schema';

const getIsUserProvisioned = () => getCookie(Cookie.ProvisionedUser)?.toLowerCase() === 'true';
const getIsConsentGiven = () => getCookie(Cookie.ConsentGiven)?.toLowerCase() === 'true';

export const SignUpForm = () => {
    const navigate = useNavigate();
    const { platformPrimaryUrl } = useAppConfiguration();

    const isProvisionedUser = getIsUserProvisioned();
    const showConsentField = !getIsConsentGiven();

    const { mutate, isPending } = useRepositoryMutation('generalUsers', 'signUp', {
        onSuccess: () => {
            void navigate({ to: '/Home/SignIn' });
            showSuccessAlert({
                description: <LocalizedText textKey={TextKey.SignUpSuccessMessage} />,
            });
        },
    });

    const onSubmit = (formData: SignUpFormValues) => {
        mutate({
            ...formData,
            consent: {
                accepted: showConsentField ? formData[FormField.AgreeToTermsAndConditions] : true,
                type: GeneralUserConsentType.HospitalityCloudTermsAndConditions,
            },
        });
    };

    const resolver = createSignUpResolver({ isProvisionedUser, showConsentField });

    return (
        <>
            <OverlayedLoader show={isPending} />
            <Form onSubmit={onSubmit} resolver={resolver}>
                <OptimusProvider density="spacious">
                    <Stack vertical spacing="200">
                        {!isProvisionedUser && (
                            <ResponsiveTwoColumns>
                                <FormInputField
                                    name={FormField.FirstName}
                                    label={<LocalizedText textKey={TextKey.FirstName} />}
                                    required
                                />
                                <FormInputField
                                    name={FormField.LastName}
                                    label={<LocalizedText textKey={TextKey.LastName} />}
                                    required
                                />
                            </ResponsiveTwoColumns>
                        )}

                        <PasswordField
                            label={<LocalizedText textKey={TextKey.Password} />}
                            helper={<LocalizedText textKey={TextKey.PasswordRequirementsErrorMessage} />}
                            name={FormField.Password}
                            autoComplete="new-password"
                            hasPasswordVisibilityToggle
                            showPasswordStrength
                            required
                        />

                        <PasswordField
                            label={<LocalizedText textKey={TextKey.ConfirmPassword} />}
                            name={FormField.NewPassword}
                            autoComplete="new-password"
                            hasPasswordVisibilityToggle
                            required
                        />

                        {showConsentField && (
                            <FormCheckboxField name={FormField.AgreeToTermsAndConditions} size="small" required>
                                <LocalizedText
                                    textKey={TextKey.AgreeWithTermsAndConditions}
                                    substitutes={{
                                        link: (linkText) => (
                                            <NewTabLink
                                                url={platformPrimaryUrl + HOSPITALITY_CLOUD_TERMS_AND_CONDITIONS_URL}
                                            >
                                                {linkText}
                                            </NewTabLink>
                                        ),
                                    }}
                                />
                            </FormCheckboxField>
                        )}

                        <Button type="submit" variant="primary" wide disabled={isPending}>
                            <LocalizedText textKey={TextKey.Continue} />
                        </Button>
                    </Stack>
                </OptimusProvider>
            </Form>
        </>
    );
};
